import { UserSetAvailabilityStatus } from '@api/generated/graphql';
import FeatureFlagModule, { FeatureFlagKeys } from '@modules/FeatureFlagModule';
import appManager from '@app/appManager';
import AppModule from '@app/lib/AppModule';
import { AuthEvents } from '@modules/AuthModule/types';
import {
  STORAGE_KEY_AUTH_REDIRECT_URL,
  STORAGE_KEY_AUTH_RESPONSE,
  STORAGE_KEY_REFRESHED_AT,
  STORAGE_KEY_SERVICE_ID,
  STORAGE_KEY_SERVICE_NAME,
  STORAGE_KEY_TASKS,
  STORAGE_PREFIX_APPAUTH,
} from '../../constants/storage';
import environment from '@lib/environment';
import AnnouncementsModule from '@modules/AnnouncementsModule';
import { authState } from '@modules/AuthModule/state';
import ServiceModule from '@modules/ServiceModule';
import SoftPhoneModule from '@modules/SoftPhoneModule';
import { phoneState } from '@modules/SoftPhoneModule/state';
import ToastModule from '@modules/ToastModule';
import { browserState } from './state';
import { strings } from './strings';
import { MicrophoneAccessType } from './types';
import { getClearBrowserDataErrorMessage } from './lib/getClearBrowserDataErrorMessage';

const MICROPHONE_ACCESS_ANNOUNCEMENT_KEY = 'microphone-access';
const MICROPHONE_CHANGE_DEBOUNCE_MS = 500;

export class BrowserModule extends AppModule {
  private _microphoneChangeCleanupFn?: () => void;
  private _microphoneChangeTimeout?: NodeJS.Timeout;

  override preload(): void {
    this.app.events.on(AuthEvents.SIGNIN, () => {
      // check for microphone accessibility
      this.requestMicrophoneAccess();

      /**
       * Registers handler to respond to microphone change events
       * provided the feature flag is enabled to gracefully handle microphone disconnect
       */
      const tryAddMicrophoneChangeHandler = () => {
        const hasGracefullyHandleMicrophoneDisconnect =
          FeatureFlagModule.isEnabled(
            FeatureFlagKeys.featureGracefullyHandleMicrophoneDisconnect,
          );

        if (hasGracefullyHandleMicrophoneDisconnect) {
          this.registerMicrophoneChangeHandler();
        }
      };

      if (FeatureFlagModule.areFlagsReady) tryAddMicrophoneChangeHandler();
      else {
        this.app.events.on(
          'featureflags.initialized',
          tryAddMicrophoneChangeHandler,
        );
      }
    });
  }

  override load(): void {
    this.requestNotificationPermission();
    window.addEventListener('unload', this.handleUnload);
  }

  public showBrowserNotification = (
    title: string,
    options?: NotificationOptions,
  ) => {
    if (Notification.permission === 'granted') {
      const notification = new Notification(title, options);

      notification.onclick = () => {
        window.focus();
        notification.close();
      };
    }
  };

  private handleUnload = async (event: BeforeUnloadEvent) => {
    if (SoftPhoneModule.isInCall()) {
      this.app.logger.info('app unloaded while in call');
      // Recommended to display the browser confirmation dialog
      event.preventDefault();

      ToastModule.showToast({
        title: strings.toasts.closingWhileInCall,
        status: 'warning',
      });

      return;
    } else {
      SoftPhoneModule.disconnectSoftPhone();
    }

    // set the user offline if they have a softphone active and they are not offline
    const user = ServiceModule.getCurrentUser();
    if (
      FeatureFlagModule.isEnabled(FeatureFlagKeys.featureOfflineOnClose) &&
      user?.id &&
      phoneState.phoneConnected &&
      ServiceModule.getAvailabilityStatus(user) !==
        UserSetAvailabilityStatus.Offline
    ) {
      event.preventDefault();
      navigator.sendBeacon(
        environment.API_URL + '/v1/tab-close',
        authState.token,
      );
      ToastModule.showToast({
        title: strings.toasts.setUserOfflineToast,
        status: 'warning',
      });
    }
  };

  private requestNotificationPermission = async () => {
    if (Notification.permission !== 'granted') {
      const permission = await Notification.requestPermission();

      if (permission !== 'granted') {
        this.app.logger.info('notification permission denied');
      }
    } else {
      this.app.logger.info('notification permission granted');
    }
  };

  /**
   * Registers a handler to reassert microphone access should device changes occur
   */
  private readonly registerMicrophoneChangeHandler = () => {
    /**
     * Clears out any currently registered handler
     **/
    const clearCurrentHandler = () => {
      const currentCleanupFn = this._microphoneChangeCleanupFn;
      this._microphoneChangeCleanupFn = undefined;
      currentCleanupFn?.();
    };

    clearCurrentHandler();
    const onDeviceChange = () => {
      clearTimeout(this._microphoneChangeTimeout);

      // initiate a debounced request to reassert microphone access
      // we debounce because a single microphone disconnect can fire multiple devicechange events
      this._microphoneChangeTimeout = setTimeout(() => {
        this._microphoneChangeTimeout = undefined;
        this.requestMicrophoneAccess();
      }, MICROPHONE_CHANGE_DEBOUNCE_MS);
    };

    // register the handler
    window.navigator.mediaDevices.addEventListener(
      'devicechange',
      onDeviceChange,
    );

    // register the cleanup function so we can handle multiple calls (edge case)
    this._microphoneChangeCleanupFn = () => {
      window.navigator.mediaDevices.removeEventListener(
        'devicechange',
        onDeviceChange,
      );
    };
  };

  public requestMicrophoneAccess = async () => {
    const hasGracefulMicrophoneDisconnect = FeatureFlagModule.isEnabled(
      FeatureFlagKeys.featureGracefullyHandleMicrophoneDisconnect,
    );

    try {
      const devices = await window.navigator.mediaDevices.getUserMedia({
        audio: true,
      });

      if (devices) {
        browserState.microphoneAccess = MicrophoneAccessType.ACCESS_GRANTED;

        if (hasGracefulMicrophoneDisconnect) {
          // clean up any previous attempts
          browserState.tries = 0;
          browserState.showMicrophoneAccessModal = false;
          AnnouncementsModule.hideAnnouncement(
            MICROPHONE_ACCESS_ANNOUNCEMENT_KEY,
          );
        }
      }
    } catch (e: unknown) {
      const isPermissionError = (e as DOMException).name === 'NotAllowedError';
      if (isPermissionError) {
        browserState.microphoneAccess = MicrophoneAccessType.PERMISSION_DENIED;
      } else {
        browserState.microphoneAccess = MicrophoneAccessType.ERROR;
      }

      browserState.tries++;
      if (browserState.tries >= 2) {
        browserState.showMicrophoneAccessModal = true;
      }

      AnnouncementsModule.showAnnouncement(
        {
          title: strings.microphoneAccess.error,
          status: 'warning',
          onClose: () =>
            AnnouncementsModule.hideAnnouncement(
              MICROPHONE_ACCESS_ANNOUNCEMENT_KEY,
            ),
          ...(hasGracefulMicrophoneDisconnect && {
            title: strings.microphoneAccess.errorSoft,
          }),
          ...(isPermissionError && {
            title: strings.microphoneAccess.noPermission,
            actionName: strings.microphoneAccess.requestPermissionCta,
            onActionClick: (): void => {
              this.requestMicrophoneAccess();
            },
          }),
        },
        MICROPHONE_ACCESS_ANNOUNCEMENT_KEY,
      );
    }
  };

  /**
   * Clears browser data (localStorage, sessionStorage, IndexedDB, caches, cookies)
   * while preserving authentication-related data and tasks.
   *
   * Note: Auth is handled via sessionStorage (auth_response), localStorage (OAuth tokens),
   * and cookies on login domains (login.servicetitan.com, login-stage.servicetitan.com).
   * Cookies on login domains cannot be cleared from this app due to browser security restrictions.
   */
  public async clearBrowserData({
    keepTasks,
  }: {
    keepTasks: boolean;
  }): Promise<void> {
    this.app.logger.info('Clearing browser data while preserving auth');

    try {
      // 1. Preserve auth data from localStorage
      const localStorageDataToPreserve = {
        serviceId: localStorage.getItem(STORAGE_KEY_SERVICE_ID),
        serviceName: localStorage.getItem(STORAGE_KEY_SERVICE_NAME),
        refreshedAt: localStorage.getItem(STORAGE_KEY_REFRESHED_AT),
        tasks: localStorage.getItem(STORAGE_KEY_TASKS),
      };

      // 2. Preserve auth data from sessionStorage
      const sessionStorageDataToPreserve = {
        auth_response: sessionStorage.getItem(STORAGE_KEY_AUTH_RESPONSE),
        auth_redirect_url: sessionStorage.getItem(
          STORAGE_KEY_AUTH_REDIRECT_URL,
        ),
      };

      // 3. Preserve OAuth AppAuth data (keys starting with STORAGE_PREFIX_APPAUTH)
      const oauthKeys: string[] = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith(STORAGE_PREFIX_APPAUTH)) {
          oauthKeys.push(key);
        }
      }
      const oauthData = oauthKeys.reduce(
        (acc, key) => {
          acc[key] = localStorage.getItem(key);
          return acc;
        },
        {} as Record<string, string | null>,
      );

      // 4. Clear localStorage
      localStorage.clear();

      // 5. Clear sessionStorage
      sessionStorage.clear();

      // 6. Restore auth data to localStorage
      if (localStorageDataToPreserve.serviceId) {
        localStorage.setItem(
          STORAGE_KEY_SERVICE_ID,
          localStorageDataToPreserve.serviceId,
        );
      }
      if (localStorageDataToPreserve.serviceName) {
        localStorage.setItem(
          STORAGE_KEY_SERVICE_NAME,
          localStorageDataToPreserve.serviceName,
        );
      }
      if (localStorageDataToPreserve.refreshedAt) {
        localStorage.setItem(
          STORAGE_KEY_REFRESHED_AT,
          localStorageDataToPreserve.refreshedAt,
        );
      }
      if (keepTasks && localStorageDataToPreserve.tasks) {
        localStorage.setItem(
          STORAGE_KEY_TASKS,
          localStorageDataToPreserve.tasks,
        );
      }

      // 7. Restore OAuth data
      Object.entries(oauthData).forEach(([key, value]) => {
        if (value) {
          localStorage.setItem(key, value);
        }
      });

      // 8. Restore auth data to sessionStorage
      if (sessionStorageDataToPreserve.auth_response) {
        sessionStorage.setItem(
          STORAGE_KEY_AUTH_RESPONSE,
          sessionStorageDataToPreserve.auth_response,
        );
      }
      if (sessionStorageDataToPreserve.auth_redirect_url) {
        sessionStorage.setItem(
          STORAGE_KEY_AUTH_REDIRECT_URL,
          sessionStorageDataToPreserve.auth_redirect_url,
        );
      }

      // 9. Clear IndexedDB (Apollo Client cache and others)
      if (window.indexedDB) {
        const databases = await window.indexedDB.databases();
        const deletionPromises = databases
          .filter((db) => db.name)
          .map(
            (db) =>
              new Promise<void>((resolve, reject) => {
                const request = window.indexedDB.deleteDatabase(db.name!);
                request.onsuccess = () => resolve();
                request.onerror = () => reject(request.error);
                request.onblocked = () => {
                  this.app.logger.warn(
                    `IndexedDB deletion blocked for ${db.name}`,
                  );
                  // Resolve anyway to not block the overall operation
                  resolve();
                };
              }),
          );
        await Promise.all(deletionPromises);
      }

      // 10. Clear Cache Storage (if Service Workers exist)
      if ('caches' in window) {
        const cacheNames = await caches.keys();
        await Promise.all(
          cacheNames.map((cacheName) => caches.delete(cacheName)),
        );
      }

      // 11. Clear all cookies accessible from this domain
      const cookies = document.cookie.split(';');

      // Parent domains where cookies may be set:
      // - .st.dev: QA environment
      // - .servicetitan.com: Stage and Production environments
      const parentDomains = ['.st.dev', '.servicetitan.com'];

      for (const cookie of cookies) {
        const cookieName = cookie.split('=')[0].trim();
        // Clear for current path without domain (browser default)
        // This handles localhost and any other domain not in the parent domains list
        document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;

        // Clear for known parent domains (QA: .st.dev, Stage/Prod: .servicetitan.com)
        for (const domain of parentDomains) {
          document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=${domain};`;
        }
      }

      this.app.logger.info('Browser data cleared successfully');
    } catch (error) {
      this.app.logger.error('Error clearing browser data', error);
      const errorMessage = getClearBrowserDataErrorMessage(error);
      throw new Error(errorMessage);
    }
  }
}

export default new BrowserModule(
  appManager.events,
  appManager.logger('BrowserModule'),
);
